import React, { useCallback, useContext, useState } from "react";
import { ErrorCode, FileRejection, useDropzone } from "react-dropzone";
import { FieldFeedback } from '@blueprint/ui';
import styled from "styled-components";
import { useSelector } from "react-redux";

import { MAX_FILE_SIZE, MIME_TYPE } from "../../../constants/files";
import { UploadedFileWithSize } from "../../../types/common";
import {
    composeErrorHandlers,
    GenericError,
} from "../../../utils/errorHandling";
import AttachmentContext from "../../contexts/AttachmentContext";
import { useAuthErrorHandlerCreator } from "../../hooks";
import { Loader } from "../core";
import { UploadFileIcon } from "../core/icons";
import PendingFileRow from "../PendingFileRow";
import ProjectContext from "../../../features/project/ProjectContext";
import { currentUserAccountNameSelector } from "../../../redux/slices/auth/selectors";

export type DropzoneProps = {
    className?: string;
    value: UploadedFileWithSize[];
    onChange: (files: UploadedFileWithSize[]) => void;
    fieldError?: string;
    setFieldError: (error?: string) => void;
    required?: boolean;
};

const Dropzone = ({
    className,
    value,
    onChange,
    fieldError,
    setFieldError,
    required,
}: DropzoneProps): JSX.Element => {
    const [error, setError] = useState<string | null>();
    const [isSubmitting, setSubmitting] = useState(false);
    const { upload, remove } = useContext(AttachmentContext);
    const { data: project } = useContext(ProjectContext);
    const currentUserAccountName = useSelector(currentUserAccountNameSelector);
    const onAuthError = useAuthErrorHandlerCreator();

    const onDrop = useCallback(
        async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
            setError(null);
            setFieldError();
            if (rejectedFiles.length > 0) {
                switch (rejectedFiles[0]?.errors[0]?.code) {
                    case ErrorCode.TooManyFiles:
                        setError("Too many files");
                        break;
                    case ErrorCode.FileInvalidType:
                        setError("Invalid file type");
                        break;
                    case ErrorCode.FileTooLarge:
                        setError("Maximum file size of 50MB has been exceeded");
                        break;
                }
            } else if (acceptedFiles.length > 0) {
                setSubmitting(true);

                const uploadedFiles: UploadedFileWithSize[] = [];
                const failedFiles: File[] = [];

                await Promise.all(
                    acceptedFiles.map((file) =>
                        upload(file, {
                            contractorAccountName:
                                currentUserAccountName ||
                                project?.mainContractors?.[0]?.accountName,
                            developerAccountName:
                                project?.developer?.accountName,
                        })
                            .then((res) =>
                                uploadedFiles.push({
                                    name: file.name,
                                    size: res.size,
                                    id: res.attId,
                                }),
                            )
                            .catch((e) => {
                                composeErrorHandlers(onAuthError, () => () => {
                                    failedFiles.push(file);
                                })(e as GenericError);
                            }),
                    ),
                );

                const nextValue = [...value, ...uploadedFiles];
                onChange(nextValue);

                const uploadedSize = nextValue.reduce(
                    (acc, cur) => acc + (cur.size || 0),
                    0,
                );
                if (failedFiles.length > 0) {
                    setError(
                        "An error has occurred. File(s) failed to upload.",
                    );
                } else if (uploadedSize >= MAX_FILE_SIZE) {
                    setFieldError(
                        "Maximum file size of 50MB has been exceeded",
                    );
                }

                setSubmitting(false);
            }
        },
        [setFieldError, value, onChange, upload, onAuthError, project, currentUserAccountName],
    );

    const handleRemoveFile = async (index: number) => {
        setSubmitting(true);
        setError(null);
        setFieldError();
        try {
            const targetFile = value[index];
            if (targetFile?.size !== undefined) {
                await remove(targetFile.id);
            }

            const nextValue = [
                ...value.slice(0, index),
                ...value.slice(index + 1),
            ];
            onChange(nextValue);

            const uploadedSize = nextValue.reduce(
                (acc, cur) => acc + (cur.size || 0),
                0,
            );
            if (uploadedSize >= MAX_FILE_SIZE) {
                setFieldError("Maximum file size of 50MB has been exceeded");
            }
        } catch (e) {
            composeErrorHandlers(onAuthError, () => () => {
                setError("An error has occurred. File failed to remove.");
            })(e as GenericError);
        }
        setSubmitting(false);
    };

    const { getRootProps, getInputProps } = useDropzone({
        accept: MIME_TYPE.PDF,
        maxSize: MAX_FILE_SIZE,
        onDrop,
    });

    return (
        <>
            <Container
                className={`mb-3 ${
                    error || fieldError ? "is-invalid" : ""
                } ${className || ""}`}
                isInvalid={!!(error || fieldError)}
                {...(isSubmitting ? {} : getRootProps())}
                required={true}

            >
                <Loader
                    overlay
                    isLoading={isSubmitting}
                    bgClassName="bg-body"
                >
                    <input {...getInputProps()} />
                    <UploadFileIcon className="icon-large mb-2" />
                    <p className="text-center fs-5 lh-base mb-0">
                        Drop your files here, or{" "}
                        <span>browse</span>
                    </p>
                    <p className="text-center text-placeholder-grey mb-0 fs-7">
                        Supports PDF only
                    </p>
                    {(error || fieldError) && (
                        <p className="mt-2 mb-0 fs-6">
                            <FieldFeedback invalid intent="error" message={error || fieldError}></FieldFeedback>
                        </p>
                    )}
                </Loader>
            </Container>
            {value.map((it, index) => (
                <PendingFileRow
                    key={index}
                    name={it.name}
                    onRemove={() => handleRemoveFile(index)}
                    disabled={isSubmitting}
                />
            ))}
        </>
    );
};

const Container = styled.div<{ isInvalid?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    box-sizing: border-box;
    border: 1px dashed var(--bs-border-grey);
    background-color: #fff;
    // border-color: ${(props) => props.isInvalid && "var(--bs-danger)"};
`;

export default Dropzone;
